import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { adminCourseGetAction, adminCourseWeeksListAction } from "../Action/action";

function badgeForStatus(status) {
  if (status === "Active") return "badge-success";
  if (status === "Coming Soon") return "badge-warning";
  if (status === "Archived") return "badge-gray";
  if (status === "Draft") return "badge-gray";
  return "badge-info";
}

export default function CourseWeeks() {
  const { courseId } = useParams();
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [perPage] = useState(10);
  const [reloadNonce, setReloadNonce] = useState(0);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [course, setCourse] = useState(null);
  const [weeks, setWeeks] = useState([]);
  const [meta, setMeta] = useState({ total: 0, page: 1, per_page: 10 });

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setError("");
      try {
        const courseRes = await adminCourseGetAction(courseId);
        if (!courseRes?.success) throw new Error(courseRes?.message || "Failed to load course.");
        if (cancelled) return;
        setCourse(courseRes.data || null);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.message || e?.message || "Failed to load course weeks.");
        if (!cancelled) {
          setCourse(null);
          setWeeks([]);
          setMeta({ total: 0, page: 1, per_page: perPage });
        }
      } finally {
        // course-only loader handled by weeks loader
      }
    }
    if (courseId) load();
    return () => {
      cancelled = true;
    };
  }, [courseId, perPage]);

  useEffect(() => {
    setPage(1);
  }, [query]);

  useEffect(() => {
    let cancelled = false;
    const q = query.trim() ? query.trim() : undefined;

    const t = setTimeout(async () => {
      setLoading(true);
      setError("");
      try {
        const weeksRes = await adminCourseWeeksListAction(courseId, { page, per_page: perPage, q });
        if (cancelled) return;
        if (!weeksRes?.success) throw new Error(weeksRes?.message || "Failed to load weeks.");

        setWeeks(Array.isArray(weeksRes.data) ? weeksRes.data : []);
        setMeta({
          total: Number(weeksRes?.meta?.total || 0),
          page: Number(weeksRes?.meta?.page || page),
          per_page: Number(weeksRes?.meta?.per_page || perPage)
        });
      } catch (e) {
        if (cancelled) return;
        setWeeks([]);
        setMeta({ total: 0, page, per_page: perPage });
        setError(e?.response?.data?.message || e?.message || "Failed to load weeks.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 350);

    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [courseId, page, perPage, query, reloadNonce]);

  const courseUi = useMemo(() => {
    if (!course) return null;
    const title = course?.title || "—";
    const status = Number(course?.active) ? "Active" : "Draft";
    return {
      id: course?.id,
      title,
      ageGroup: course?.age_group || "—",
      price: course?.price !== undefined && course?.price !== null ? String(course.price) : "—",
      weeksCount: course?.weeks_count !== undefined && course?.weeks_count !== null ? Number(course.weeks_count) : "—",
      status
    };
  }, [course]);

  const weekRows = useMemo(() => {
    return weeks.map((w, idx) => {
      const num = w?.position !== undefined && w?.position !== null ? String(w.position) : String(idx + 1);
      const title = w?.title || "—";
      const summary = w?.subtitle || w?.short_description || "—";
      const sectionsCount = w?.sections_count !== undefined && w?.sections_count !== null ? Number(w.sections_count) : null;
      const sectionsTotal = w?.sections_total !== undefined && w?.sections_total !== null ? Number(w.sections_total) : null;
      const videosCount = w?.videos_count !== undefined && w?.videos_count !== null ? Number(w.videos_count) : null;
      return {
        raw: w,
        id: w?.id,
        num,
        title,
        summary,
        sectionsCount,
        sectionsTotal,
        videosCount,
        unlockLabel: idx === 0 ? "Immediate" : `Day ${idx * 7 + 1}`,
        unlockIsImmediate: idx === 0
      };
    });
  }, [weeks]);

  const total = meta?.total ? Number(meta.total) : weekRows.length;
  const totalPages = total ? Math.max(1, Math.ceil(total / perPage)) : 1;
  const start = total && weekRows.length ? (page - 1) * perPage + 1 : 0;
  const end = total && weekRows.length ? start + weekRows.length - 1 : 0;

  const pageButtons = useMemo(() => {
    const max = 5;
    const safeTotal = Math.max(1, totalPages);
    const current = Math.min(Math.max(1, page), safeTotal);
    let s = Math.max(1, current - 2);
    let e = Math.min(safeTotal, s + max - 1);
    s = Math.max(1, e - max + 1);
    const arr = [];
    for (let i = s; i <= e; i += 1) arr.push(i);
    return arr;
  }, [page, totalPages]);

  const clearAndReload = () => {
    setQuery("");
    setPage(1);
    setError("");
    setReloadNonce((n) => n + 1);
  };

  return (
    <div className="content">
      {error ? (
        <div className="banner banner-warning">
          <span className="banner-icon">⚠️</span>
          <div className="banner-text">
            <strong>Course weeks unavailable.</strong> {error}
          </div>
        </div>
      ) : null}

      <div className="breadcrumb">
        <Link to="/courses">Courses</Link>
        <span>›</span>
        <Link to={`/courses/${courseId}/edit`}>{courseUi?.title || "Course"}</Link>
        <span>›</span>
        <span>Manage Weeks</span>
      </div>

      <div className="page-banner">
        <div className="page-banner-content">
          <div className="page-banner-icon blue">{String(courseUi?.title || "C").slice(0, 1).toUpperCase()}</div>
          <div className="page-banner-text">
            <h2>{courseUi?.title || "—"}</h2>
            <p>
              {courseUi?.ageGroup || "—"} ·{" "}
              {courseUi?.price && courseUi?.price !== "—" ? `AUD ${courseUi?.price}` : "—"} ·{" "}
              {courseUi?.weeksCount !== undefined && courseUi?.weeksCount !== null && courseUi?.weeksCount !== "—"
                ? `${courseUi?.weeksCount} Weeks`
                : "—"}{" "}
              ·{" "}
              <span className={`badge ${badgeForStatus(courseUi?.status)} v-middle`}>{courseUi?.status || "—"}</span>
            </p>
          </div>
        </div>
        <div className="flex gap-3">
          <Link to={`/courses/${courseId}/edit`} className="btn btn-outline btn-sm">
            ✏️ Edit Course
          </Link>
          <Link to="new" className="btn btn-primary btn-sm">
            ➕ Add Week
          </Link>
        </div>
      </div>

      <div className="banner banner-warning">
        <span className="banner-icon">⏰</span>
        <div className="banner-text">
          <strong>Weekly unlock:</strong> Each week automatically unlocks 7 days after the previous one. Week 1 is available immediately after purchase.
        </div>
      </div>

      <div className="toolbar">
        <div className="flex items-center gap-3">
          <div className="search-box">
            <input
              type="text"
              placeholder="Search weeks by title..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <button className="btn btn-outline" type="button" onClick={clearAndReload} disabled={loading}>
            ↻ Clear
          </button>
        </div>
      </div>

      {loading ? (
        <div className="empty-state" style={{ padding: "28px 12px" }}>
          Loading…
        </div>
      ) : weekRows.length === 0 ? (
        <div className="empty-state" style={{ padding: "28px 12px" }}>
          <div className="empty-icon">🫥</div>
          No weeks found for this course.
        </div>
      ) : (
        <>
          {weekRows.map((w) => (
            <div key={w.id} className={`week-card ${w.sectionsCount === 0 ? "empty" : ""}`}>
              <div className="drag-handle">⠿</div>
              <div className="week-number">{w.num}</div>
              <div className="week-info">
                <h4>{w.title}</h4>
                <p>{w.summary}</p>
              </div>
              <div className="week-meta">
                <div className="meta-item">
                  <span>Sections</span>
                  <strong>
                    {w.sectionsCount !== null ? `${w.sectionsCount}` : "—"}
                  </strong>
                </div>
                <div className="meta-item">
                  <span>Videos</span>
                  <strong>{w.videosCount !== null ? String(w.videosCount) : "—"}</strong>
                </div>
                <div className="meta-item">
                  <span>Unlock</span>
                  <strong className={`${w.unlockIsImmediate ? "text-success" : ""} font-sm`}>{w.unlockLabel}</strong>
                </div>
              </div>
              <Link to={`${w.id}`} className={`btn ${w.sectionsCount === 0 ? "btn-accent" : "btn-primary"} btn-sm`}>
                {w.sectionsCount === 0 ? "Add Content" : "Edit Content"}
              </Link>
            </div>
          ))}

          <div className="pagination">
            <span>
              Showing {start}-{end} of {total} Weeks
            </span>
            <div className="pagination-pages">
              <button
                type="button"
                onClick={() => setPage(1)}
                disabled={page <= 1 || loading}
                title="First page"
                aria-label="First page"
              >
                {"<<"}
              </button>
              <button type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1 || loading}>
                ‹
              </button>
              {pageButtons.map((p) => (
                <button
                  key={p}
                  type="button"
                  className={p === page ? "active" : ""}
                  onClick={() => setPage(p)}
                  disabled={loading}
                >
                  {p}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages || loading}
              >
                ›
              </button>
              <button
                type="button"
                onClick={() => setPage(totalPages)}
                disabled={page >= totalPages || loading}
                title="Last page"
                aria-label="Last page"
              >
                {">>"}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

